import { useState, type ReactNode } from "react";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import useFiles from "../../hooks/use-files";
import styles from "./DocumentShowList.module.css";
import DocumentCard from "./DocumentCard";
// @ts-expect-error pure js
import DocumentsFormUpload from "./DocumentsFormUpload";
// @ts-expect-error pure js
import { DOCUMENT_LIST } from "../../models/documents";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Button from "../UI/Button";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { useToast } from "../UI/Toast/useToast";
import ModalDialog, { ModalDialogBody } from "../UI/ModalDialog";
import Text from "../UI/Text";
import {
  IconEye,
  IconFileExport,
  IconPlus,
  IconUsers,
} from "@tabler/icons-react";
import ViewModeToggle from "../UI/ViewModeToggle";
import { isViewMode, type ViewMode } from "../UI/viewMode";
import useLocalStorageState from "../../hooks/use-local-storage-state";
import DocumentListView, { type RenderFileOptions } from "./DocumentListView";
import Card from "../UI/Card";
import DocumentSelectionBar from "./DocumentSelectionBar";
import CharteColocationForm from "../Property/CharteColocationForm";
import type { DocumentListEntry } from "../../utils/documentList";
import type { DocumentFile } from "../../models/document";
import type { Tenant } from "../../models/tenant";
import {
  PropertyStatus,
  PropertyType,
  type Property,
} from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import {
  fileCoversDiagnostic,
  getDiagnosticStatus,
} from "../../utils/documentStatus";
import {
  DIAGNOSTIC_SHORT_LABELS,
  getDiagnosticRequirement,
  type DiagnosticRequirement,
} from "../../utils/diagnosticRules";
// @ts-expect-error pure js
import { FileTag } from "../../utils/constants/documents";
import MandatoryDiagnostics, {
  type DiagnosticEntry,
} from "./MandatoryDiagnostics";
import Title from "../UI/Title";
// @ts-expect-error pure js
import { isDiagnosticTag } from "../../models/documents";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";

interface DocumentDef {
  tag: string;
  nom: string;
  groupTags?: string[];
  requiredFiscalite?: boolean;
  requiredProperty?: boolean;
  requiredTenant?: boolean;
  mandatoryProperty?: boolean;
  vueBailleur?: boolean;
  addLocataire?: boolean;
  addProperty?: boolean;
}

interface DocumentShowListProps {
  propertyFolder?: PropertyFolder;
  property?: Property;
  tenant?: Tenant;
  profileBailleurId?: number;
  allowDelete?: boolean;
  isBailleur?: boolean;
  isLocataire?: boolean;
  allowEdit?: boolean;
  fiscalite?: boolean;
  className?: string;
}

interface DetailFilesState {
  type?: string;
  titre?: string;
  groupTags?: string[];
}

// Un diagnostic est aussi couvert par un PDF regroupant plusieurs diagnostics.
function documentMatchesFile(
  document: Pick<DocumentDef, "tag" | "groupTags">,
  file: DocumentFile,
): boolean {
  if (document.groupTags) {
    return document.groupTags.includes(file.tag);
  }
  return fileCoversDiagnostic(file, document.tag);
}

const VIEW_MODE_STORAGE_KEY = "documents-view-mode";

interface UseFilesReturn {
  files: DocumentFile[];
  deleteFile: (fileId: number) => Promise<unknown>;
  deleteFiles: (fileIds: number[]) => Promise<unknown>;
  updateFiles: (
    fileIds: number[],
    values: Record<string, unknown>,
  ) => Promise<unknown>;
  sendToElectronicSigning: (fileId: number) => Promise<unknown>;
  updateFile: (
    fileId: number,
    values: Record<string, unknown>,
  ) => Promise<unknown>;
  cancelElectronicSigning: (fileId: number) => Promise<unknown>;
  relanceSignataire: (fileId: number, signataireId: number) => Promise<unknown>;
  sendToLaPoste: (
    fileId: number,
    values: Record<string, unknown>,
  ) => Promise<unknown>;
  isLoading: boolean;
  mutate: () => Promise<unknown>;
}

export default function DocumentShowList({
  propertyFolder,
  property,
  tenant,
  profileBailleurId,
  allowDelete,
  isBailleur,
  isLocataire,
  allowEdit,
  fiscalite,
  className,
}: DocumentShowListProps) {
  const {
    files,
    deleteFile,
    deleteFiles,
    updateFiles,
    sendToElectronicSigning,
    updateFile,
    cancelElectronicSigning,
    relanceSignataire,
    sendToLaPoste,
    isLoading,
    mutate,
  }: UseFilesReturn = useFiles({
    tenantId: tenant?.id,
    propertyId: isLocataire ? undefined : property?.id,
    isTenant: isLocataire,
    propertyFolderId: isLocataire ? undefined : propertyFolder?.id,
    profileBailleurId,
  });
  const filesFiltered = files.filter((file) =>
    isLocataire
      ? true
      : property || propertyFolder
      ? !file.id_tenant
      : profileBailleurId
      ? !file.id_tenant && !file.id_bien && !file.id_property_folder
      : true,
  );
  const axios = useAuthenticatedAxios();
  const toast = useToast();
  const { properties, updateProperty } = useProperties() as {
    properties: Property[];
    updateProperty: (
      propertyId: number,
      values: Partial<Property>,
    ) => Promise<unknown>;
  };
  const [detailOpen, setDetailOpen] = useState(false);
  const [DocumentModal, closeDocumentModal, openDocumentModal] = useModal();
  const [detailFiles, setDetailFiles] = useState<DetailFilesState>({});
  const [uploadTag, setUploadTag] = useState<string | undefined>(undefined);
  const [isCharteOpen, setIsCharteOpen] = useState(false);
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    VIEW_MODE_STORAGE_KEY,
    "cards",
    isViewMode,
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  if (isLoading) return <IsLoading />;

  // La déclaration fiscale montre une card par document attendu, même vide : pas de vue liste.
  const isListMode = viewMode === "list" && !fiscalite;
  // Un document hérité de l'immeuble se gère depuis l'immeuble : il reste hors sélection.
  const isSelectable = (file: DocumentFile) =>
    !(property && file.id_property_folder);
  const showShareColumn = Boolean(allowEdit && !tenant && !isLocataire);

  const sortedFiles = [...filesFiltered].sort((a, b) =>
    (a.meta?.date_quittance ?? "") < (b.meta?.date_quittance ?? "") ? 1 : -1,
  );

  async function deleteDocumentHandler(fileId: number) {
    try {
      await deleteFile(fileId);
    } catch (error) {
      console.error(error);
    }
  }

  const selectedFiles = sortedFiles.filter((file) => selectedIds.has(file.id));

  function changeSelection(fileIds: number[], selected: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      fileIds.forEach((id) => (selected ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  function changeViewMode(mode: ViewMode) {
    setViewMode(mode);
    setSelectedIds(new Set());
  }

  async function deleteSelected(toDelete: DocumentFile[]): Promise<boolean> {
    try {
      await deleteFiles(toDelete.map((file) => file.id));
      setSelectedIds(new Set());
      toast.success({
        title: `${toDelete.length} document${
          toDelete.length > 1 ? "s" : ""
        } supprimé${toDelete.length > 1 ? "s" : ""}`,
      });
      return true;
    } catch (error) {
      toast.error({
        title: "Impossible de supprimer les documents",
        description: handleAPIError(error),
      });
      return false;
    }
  }

  async function shareSelected(toShare: DocumentFile[]) {
    try {
      await updateFiles(
        toShare.map((file) => file.id),
        { shared: true },
      );
      setSelectedIds(new Set());
    } catch (error) {
      toast.error({
        title: "Impossible de partager les documents",
        description: handleAPIError(error),
      });
    }
  }

  const renderDocumentCard = (
    document: DocumentDef,
    docFiles: DocumentFile[],
    i: string | number,
    row?: RenderFileOptions,
  ) => {
    const rowProps = row
      ? {
          layout: "row" as const,
          selected: docFiles.length === 1 && selectedIds.has(docFiles[0].id),
          onSelectedChange: (fileId: number, selected: boolean) =>
            changeSelection([fileId], selected),
          child: row.child,
          last: row.last,
          showShareColumn,
        }
      : {};
    // Dans un dossier déplié, le type du document est déjà sur la ligne du dossier :
    // chaque fichier porte son propre nom.
    const titre =
      row?.child && document.tag !== "autre"
        ? docFiles[0]?.filename ?? document.nom
        : document.nom;
    if (document.tag === "autre") {
      return docFiles.map((file, j) => (
        <DocumentCard
          key={`${i}${j}`}
          files={[file]}
          titre={file.filename ?? ""}
          deleteDocumentHandler={
            allowDelete ? deleteDocumentHandler : undefined
          }
          property={property}
          propertyFolder={propertyFolder}
          isBailleur={isBailleur}
          tenant={tenant}
          updateFile={allowEdit ? updateFile : undefined}
          sendToElectronicSigning={
            document.tag !== "caution" && isBailleur
              ? sendToElectronicSigning
              : undefined
          }
          sendToLaPoste={isBailleur ? sendToLaPoste : undefined}
          cancelElectronicSigning={cancelElectronicSigning}
          relanceSignataire={relanceSignataire}
          allowEdit={allowEdit}
          profileId={profileBailleurId}
          {...rowProps}
        />
      ));
    }
    return (
      <DocumentCard
        key={i}
        files={docFiles}
        titre={titre}
        tag={document.tag}
        deleteDocumentHandler={allowDelete ? deleteDocumentHandler : undefined}
        property={property}
        propertyFolder={propertyFolder}
        isLocataire={isLocataire}
        isBailleur={isBailleur}
        tenant={tenant}
        updateFile={allowEdit ? updateFile : undefined}
        sendToElectronicSigning={
          document.tag !== "caution" && isBailleur
            ? sendToElectronicSigning
            : undefined
        }
        sendToLaPoste={isBailleur ? sendToLaPoste : undefined}
        onDetailFiles={() => {
          setDetailFiles({
            type: document.tag,
            titre: document.nom,
            groupTags: document.groupTags,
          });
          setDetailOpen(true);
        }}
        cancelElectronicSigning={cancelElectronicSigning}
        relanceSignataire={relanceSignataire}
        allowEdit={allowEdit}
        profileId={profileBailleurId}
        {...rowProps}
      />
    );
  };

  // Ligne de la vue liste : le type du fichier donne le titre, sauf pour « autre » (nom du fichier).
  const renderFileRow = (file: DocumentFile, row: RenderFileOptions) => {
    const document =
      (DOCUMENT_LIST as DocumentDef[]).find((candidate) =>
        documentMatchesFile(candidate, file),
      ) ?? ({ tag: "autre", nom: file.filename ?? "" } as DocumentDef);
    return renderDocumentCard(document, [file], file.id, row);
  };

  // Diagnostics attendus d'après les caractéristiques du bien, sans aucun
  // fichier : proposés en ajout rapide, le formulaire s'ouvre prérempli. Une
  // colocation par chambres est un dossier : ses chambres (mêmes
  // caractéristiques) portent les règles et les dispenses, appliquées à toutes.
  // Rien n'est demandé sur un immeuble, chaque lot porte ses diagnostics.
  const isColocFolder =
    propertyFolder?.property_type === PropertyType.BIEN_COMPTABLE;
  const canCreateCharte = isColocFolder && !isLocataire && !fiscalite;
  const diagnosticSubjects: Property[] = property
    ? [property]
    : isColocFolder
    ? properties.filter(
        (candidate) =>
          candidate.id_property_folder === propertyFolder?.id &&
          candidate.status === PropertyStatus.Actif.status,
      )
    : [];
  const showMandatory =
    !fiscalite && !isLocataire && diagnosticSubjects.length > 0;
  const mandatoryDocs = showMandatory
    ? (DOCUMENT_LIST as DocumentDef[]).filter(
        (document) => document.mandatoryProperty,
      )
    : [];
  const isNotApplicable = (subject: Property, tag: string) =>
    (subject.not_applicable_diagnostics ?? []).includes(tag);
  const notApplicableTags = mandatoryDocs
    .map((document) => document.tag)
    .filter(
      (tag) =>
        diagnosticSubjects.length > 0 &&
        diagnosticSubjects.every((subject) => isNotApplicable(subject, tag)),
    );
  const requirementFor = (tag: string): DiagnosticRequirement => {
    const requirements = diagnosticSubjects
      .filter((subject) => !isNotApplicable(subject, tag))
      .map((subject) => getDiagnosticRequirement(subject, tag));
    return (
      requirements.find((requirement) => requirement.required) ??
      requirements[0] ?? { required: false }
    );
  };
  const hasFile = (document: DocumentDef) =>
    sortedFiles.some((file) => documentMatchesFile(document, file));
  const requiredDocs = mandatoryDocs.filter(
    (document) =>
      requirementFor(document.tag).required &&
      !notApplicableTags.includes(document.tag),
  );
  const toEntry = (
    document: DocumentDef,
    reason?: string,
  ): DiagnosticEntry => ({
    tag: document.tag,
    label: DIAGNOSTIC_SHORT_LABELS[document.tag] ?? document.nom,
    name: document.nom,
    reason,
  });
  const missingDiagnostics = requiredDocs
    .filter((document) => !hasFile(document))
    .map((document) =>
      toEntry(document, requirementFor(document.tag).reason ?? document.nom),
    );
  const providedCount = requiredDocs.filter((document) =>
    ["valide", "bientotExpire"].includes(
      getDiagnosticStatus(sortedFiles, document.tag).status,
    ),
  ).length;
  const notRequiredDiagnostics = mandatoryDocs
    .filter((document) => !requiredDocs.includes(document))
    .map((document) =>
      toEntry(
        document,
        notApplicableTags.includes(document.tag)
          ? undefined
          : requirementFor(document.tag).reason,
      ),
    );
  const subjectLabel = !property
    ? "cette colocation"
    : property.type === "habitation"
    ? "ce logement"
    : "ce local";

  async function setNotApplicable(tag: string, notApplicable: boolean) {
    try {
      await Promise.all(
        diagnosticSubjects.map((subject) => {
          const current = subject.not_applicable_diagnostics ?? [];
          const next = notApplicable
            ? current.includes(tag)
              ? current
              : [...current, tag]
            : current.filter((t) => t !== tag);
          return updateProperty(subject.id, {
            not_applicable_diagnostics: next,
          });
        }),
      );
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer la dispense de diagnostic",
        description: handleAPIError(error),
      });
    }
  }

  interface CardEntry {
    document: DocumentDef;
    files: DocumentFile[];
    index: string | number;
  }
  const folders: CardEntry[] = [];
  const singleFiles: CardEntry[] = [];
  const providedDiagnostics: CardEntry[] = [];
  const isDiagnosticDocument = (document: DocumentDef) =>
    isDiagnosticTag(document.tag) || document.tag === FileTag.POLLUTION;

  (DOCUMENT_LIST as DocumentDef[]).forEach((document, i) => {
    const docFiles = sortedFiles.filter((file) =>
      documentMatchesFile(document, file),
    );
    const shouldShowFileCard = fiscalite
      ? (!!property || !!propertyFolder) && document.requiredFiscalite
      : docFiles.length > 0 ||
        ((!!property || !!propertyFolder) && document.requiredProperty) ||
        (!!tenant && document.requiredTenant && isBailleur) ||
        (!!profileBailleurId &&
          document.vueBailleur &&
          !document.addLocataire &&
          !document.addProperty);

    if (!shouldShowFileCard) return;
    if (!fiscalite && docFiles.length === 0) return;

    if (document.tag === "autre") {
      docFiles.forEach((file, j) => {
        singleFiles.push({ document, files: [file], index: `${i}${j}` });
      });
    } else if (showMandatory && isDiagnosticDocument(document)) {
      providedDiagnostics.push({ document, files: docFiles, index: i });
    } else if (docFiles.length > 1) {
      folders.push({ document, files: docFiles, index: i });
    } else {
      singleFiles.push({ document, files: docFiles, index: i });
    }
  });

  const otherDocumentsCount = [...folders, ...singleFiles].reduce(
    (total, entry) => total + entry.files.length,
    0,
  );

  const toListEntry = ({ document, files }: CardEntry): DocumentListEntry =>
    files.length > 1
      ? {
          key: `folder-${document.tag}`,
          kind: "folder",
          title: document.nom,
          files,
        }
      : {
          key: `file-${files[0].id}`,
          kind: "file",
          title:
            document.tag === "autre" ? files[0].filename ?? "" : document.nom,
          files,
        };
  // En fiscalité, un document requis sans fichier est une carte « Ajouter le
  // document » : la vue liste n'y existe pas, donc pas de ligne à en tirer.
  const documentEntries = isListMode
    ? [...folders, ...singleFiles].map(toListEntry)
    : [];
  const diagnosticEntries = isListMode
    ? providedDiagnostics.map(toListEntry)
    : [];

  const renderList = (
    label: string,
    entries: DocumentListEntry[],
    downloadZip: (files: DocumentFile[]) => void,
    missing?: DiagnosticEntry[],
  ) => (
    <DocumentListView
      label={label}
      entries={entries}
      missing={missing}
      renderFile={renderFileRow}
      selectedIds={selectedIds}
      onSelectionChange={changeSelection}
      isSelectable={isSelectable}
      showShareColumn={showShareColumn}
      onDownloadZip={downloadZip}
      onAddMissing={
        missing
          ? (tag) => {
              setUploadTag(tag);
              openDocumentModal();
            }
          : undefined
      }
      onSetNotApplicable={missing ? setNotApplicable : undefined}
      profileBailleurId={profileBailleurId}
    />
  );

  return (
    <>
      <ModalDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        size="xl"
        header={detailFiles.titre}
      >
        <ModalDialogBody>
          <div className={styles.filesGrid}>
            {sortedFiles
              .filter(
                (file) =>
                  detailFiles.type !== undefined &&
                  documentMatchesFile(
                    { tag: detailFiles.type, groupTags: detailFiles.groupTags },
                    file,
                  ),
              )
              .map((file, i) => (
                <DocumentCard
                  files={[file]}
                  titre={file.filename ?? ""}
                  deleteDocumentHandler={
                    allowDelete ? deleteDocumentHandler : undefined
                  }
                  property={property}
                  isBailleur={isBailleur}
                  isLocataire={isLocataire}
                  tenant={tenant}
                  updateFile={allowEdit ? updateFile : undefined}
                  sendToElectronicSigning={
                    file.tag !== "caution" && isBailleur
                      ? sendToElectronicSigning
                      : undefined
                  }
                  sendToLaPoste={isBailleur ? sendToLaPoste : undefined}
                  cancelElectronicSigning={cancelElectronicSigning}
                  relanceSignataire={relanceSignataire}
                  allowEdit={allowEdit}
                  profileId={profileBailleurId}
                  key={i}
                />
              ))}
          </div>
        </ModalDialogBody>
      </ModalDialog>
      <DocumentModal>
        <DocumentsFormUpload
          property={property}
          tenant={tenant}
          propertyFolder={propertyFolder}
          profileBailleurId={profileBailleurId}
          onSubmitted={closeDocumentModal}
          isBailleur={isBailleur}
          isLocataire={isLocataire}
          typeDocument={uploadTag}
        />
      </DocumentModal>
      {canCreateCharte && propertyFolder && (
        <CharteColocationForm
          open={isCharteOpen}
          onOpenChange={setIsCharteOpen}
          propertyFolder={propertyFolder}
          onGenerated={() => void mutate()}
        />
      )}
      <DocumentDownloader>
        {(setSignedUrl: (url: string) => void) => {
          const downloadZip = async (toZip: DocumentFile[]) => {
            try {
              const response = await axios.post("/documents/zip", {
                file_ids: toZip.map((file) => file.id),
              });
              setSignedUrl(response.data);
            } catch (error) {
              toast.error({
                title: "Impossible d'exporter les documents",
                description: handleAPIError(error),
              });
            }
          };
          const diagnosticsList: ReactNode =
            diagnosticEntries.length + missingDiagnostics.length > 0 ? (
              renderList(
                "Diagnostics obligatoires",
                diagnosticEntries,
                downloadZip,
                missingDiagnostics,
              )
            ) : (
              <Text color="muted">
                Aucun diagnostic à fournir pour {subjectLabel}.
              </Text>
            );
          const actions = !fiscalite && (
            <div className={styles.actionsRow}>
              <Button
                onClick={() => {
                  setUploadTag(undefined);
                  openDocumentModal();
                }}
                icon={IconPlus}
                requiresWrite
                profileId={profileBailleurId}
              >
                Ajouter un document
              </Button>
              {canCreateCharte && propertyFolder && (
                <Button
                  variant="secondary"
                  icon={IconUsers}
                  onClick={() => setIsCharteOpen(true)}
                  requiresWrite
                  profileId={propertyFolder.id_profile_nom_owner}
                >
                  Créer une charte de colocation
                </Button>
              )}
              {sortedFiles.length > 0 && (
                <Button
                  variant="secondary"
                  icon={IconFileExport}
                  onClick={() => downloadZip(sortedFiles)}
                >
                  Tout exporter (ZIP)
                </Button>
              )}
              <ViewModeToggle
                value={viewMode}
                onChange={changeViewMode}
                className={styles.viewToggle}
              />
            </div>
          );
          const content = (
            <>
              {showMandatory && (
                <MandatoryDiagnostics
                  layout={isListMode ? "list" : "cards"}
                  missing={missingDiagnostics}
                  provided={providedDiagnostics.map(
                    ({ document, files, index }) =>
                      renderDocumentCard(document, files, index),
                  )}
                  notRequired={notRequiredDiagnostics}
                  providedCount={providedCount}
                  requiredCount={requiredDocs.length}
                  subjectLabel={subjectLabel}
                  profileBailleurId={profileBailleurId}
                  onAdd={(tag) => {
                    setUploadTag(tag);
                    openDocumentModal();
                  }}
                  onSetNotApplicable={setNotApplicable}
                >
                  {isListMode && diagnosticsList}
                </MandatoryDiagnostics>
              )}

              {showMandatory && (
                <div className={styles.othersHeader}>
                  <Title variant="hecto" weight="bold">
                    Autres documents
                  </Title>
                  <Text color="muted">
                    {otherDocumentsCount} document
                    {otherDocumentsCount > 1 ? "s" : ""}
                  </Text>
                </div>
              )}

              {propertyFolder && (
                <Text variant="deci" className={styles.folderHint}>
                  Les documents partagés au locataire seront visibles par tous
                  les locataires des biens de cet immeuble.
                </Text>
              )}

              {isListMode ? (
                documentEntries.length > 0 ? (
                  renderList("Autres documents", documentEntries, downloadZip)
                ) : (
                  <Text color="muted">Aucun document.</Text>
                )
              ) : (
                <>
                  {folders.length > 0 && (
                    <div className={classnames(styles.foldersGrid, className)}>
                      {folders.map(({ document, files, index }) =>
                        renderDocumentCard(document, files, index),
                      )}
                    </div>
                  )}

                  <div className={classnames(styles.filesGrid, className)}>
                    {singleFiles.map(({ document, files, index }) =>
                      renderDocumentCard(document, files, index),
                    )}
                  </div>
                </>
              )}

              {property && (
                <Text color="muted" className={styles.sharedHint}>
                  <IconEye size={16} /> Les documents partagés sont visibles par
                  le locataire depuis son espace.
                </Text>
              )}

              {isListMode && (
                <DocumentSelectionBar
                  files={selectedFiles}
                  onClear={() => setSelectedIds(new Set())}
                  onDownloadZip={downloadZip}
                  onShare={showShareColumn ? shareSelected : undefined}
                  onDelete={allowDelete ? deleteSelected : undefined}
                  profileBailleurId={profileBailleurId}
                />
              )}
            </>
          );
          // En vue liste, les documents se posent dans une card, comme les tableaux de l'app,
          // la rangée de boutons restant au-dessus ; l'espace locataire est déjà dans une card.
          return (
            <>
              {actions}
              {isListMode && !isLocataire ? (
                <Card padding="md">{content}</Card>
              ) : (
                content
              )}
            </>
          );
        }}
      </DocumentDownloader>
    </>
  );
}
